<template>
  <b-card
    v-if="!hidden"
    border-variant="light"
    bg-variant="light"
    body-class="px-3 py-3"
  >
    <div class="d-flex align-items-center justify-content-between flex-wrap">
      <div class="d-flex align-items-center mb-2 mb-sm-0">
        <span
          class="status-dot mr-2"
          :class="clockedIn ? 'status-dot--on' : 'status-dot--off'"
        />
        <div>
          <div class="font-weight-bold">
            {{ clockedIn ? 'Clocked In' : 'Not Clocked In' }}
          </div>
          <div v-if="clockedIn" class="text-muted small">
            {{ activeEvent.project && activeEvent.project.name }}
            <span v-if="elapsed"> · {{ elapsed }}</span>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center">
        <b-button
          variant="link"
          size="sm"
          class="mr-2"
          @click="$bvModal.show('my-timesheet-history-modal')"
        >
          History
        </b-button>
        <b-button
          :variant="clockedIn ? 'outline-danger' : 'primary'"
          :disabled="loading"
          @click="openDialog"
        >
          {{ clockedIn ? 'Clock Out' : 'Clock In' }}
        </b-button>
      </div>
    </div>

    <b-alert :show="!!warning" variant="warning" class="mt-3 mb-0">
      {{ warning }}
    </b-alert>

    <clock-in-dialog :projects="projects" @clocked-in="onClockedIn" />
    <clock-out-dialog
      :active-event="activeEvent"
      :project="activeProject"
      @clocked-out="onClockedOut"
    />
    <timesheet-history-modal :projects="projects" />
  </b-card>
</template>

<script>
import { mapActions } from 'vuex'
import ClockInDialog from './ClockInDialog'
import ClockOutDialog from './ClockOutDialog'
import TimesheetHistoryModal from './TimesheetHistoryModal'

export default {
  name: 'ClockInOutCard',

  components: { ClockInDialog, ClockOutDialog, TimesheetHistoryModal },

  data() {
    return {
      loading: false,
      hidden: false,
      projects: [],
      activeEvent: null,
      warning: null,
      elapsedTimer: null,
      elapsed: '',
    }
  },

  computed: {
    clockedIn() {
      return !!this.activeEvent
    },

    activeProject() {
      if (!this.activeEvent) return null
      return (
        this.projects.find((p) => p.id === this.activeEvent.project_id) ||
        this.activeEvent.project ||
        null
      )
    },
  },

  async mounted() {
    this.loading = true
    try {
      const [projects, status] = await Promise.all([
        this.getMyTimesheetProjects(),
        this.getMyTimesheetStatus(),
      ])
      this.projects = projects
      this.activeEvent = status
      this.startElapsedTimer()
    } catch (e) {
      // No company / no active token — this tradie isn't eligible for
      // self clock-in at all, so hide the card rather than show an error.
      const reason = e.response && e.response.data && e.response.data.reason
      if (
        e.response &&
        e.response.status === 403 &&
        (reason === 'tradie_no_access' || reason === 'tradie_not_found')
      ) {
        this.hidden = true
      } else {
        this.warning = 'Could not load your timesheet status - try refreshing.'
      }
    } finally {
      this.loading = false
    }
  },

  beforeDestroy() {
    if (this.elapsedTimer) clearInterval(this.elapsedTimer)
  },

  methods: {
    ...mapActions('timesheet', [
      'getMyTimesheetProjects',
      'getMyTimesheetStatus',
    ]),

    async openDialog() {
      this.warning = null
      // Projects (and their supervisors) are only fetched on mount, so a
      // supervisor added to a project after this page loaded would
      // otherwise never show up in the Clock In dialog without a hard
      // refresh — refetch here so this stays in step with what the
      // Flutter app already does on every home-screen visit/resume.
      if (!this.clockedIn) {
        try {
          this.projects = await this.getMyTimesheetProjects()
        } catch (e) {
          // Fall back to whatever was already loaded rather than blocking
          // the dialog from opening at all.
        }
      }
      this.$bvModal.show(
        this.clockedIn ? 'self-clock-out-modal' : 'self-clock-in-modal',
      )
    },

    onClockedIn(event) {
      this.activeEvent = event
      this.startElapsedTimer()
    },

    onClockedOut() {
      this.activeEvent = null
      if (this.elapsedTimer) clearInterval(this.elapsedTimer)
      this.elapsed = ''
    },

    startElapsedTimer() {
      if (this.elapsedTimer) clearInterval(this.elapsedTimer)
      if (!this.activeEvent) return

      const update = () => {
        const start = new Date(
          this.activeEvent.clocked_in_at ||
            this.activeEvent.selected_clocked_in_at,
        ).getTime()
        const diffMs = Date.now() - start
        const hours = Math.floor(diffMs / 3600000)
        const mins = Math.floor((diffMs % 3600000) / 60000)
        this.elapsed = `${hours}h ${mins}m`
      }
      update()
      this.elapsedTimer = setInterval(update, 60000)
    },
  },
}
</script>

<style lang="scss" scoped>
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;

  &--on {
    background: #28a745;
  }

  &--off {
    background: #adb5bd;
  }
}
</style>
